import React, { useContext, useEffect, useRef, useState, useMemo } from 'react'
import { Button, Collapse, Paper, useTheme, Select, MenuItem, Chip, Snackbar, Menu } from '@mui/material'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import delay from 'delay'
import moment from 'moment'
import anime from 'animejs'
import Drawer from '@mui/material/Drawer'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseIcon from '@mui/icons-material/Close'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import GppMaybeIcon from '@mui/icons-material/GppMaybe'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import WatchLaterIcon from '@mui/icons-material/WatchLater'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import DeleteIcon from '@mui/icons-material/Delete'
import RefreshIcon from '@mui/icons-material/Refresh'
import CheckIcon from '@mui/icons-material/Check'
import ShieldIcon from '@mui/icons-material/Shield'
import BlockIcon from '@mui/icons-material/Block'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import ReplayIcon from '@mui/icons-material/Replay'
import PlaylistRemoveIcon from '@mui/icons-material/PlaylistRemove'
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep'
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import { alpha } from '@mui/material/styles'
import { ApiDataWrapperContext } from '../../contexts/ApiDataWrapper'
import MetaAccountHealthBanner from '../MetaAccountHealthBanner'
import { isMetaOfficialProvider } from '../../libs/metaTemplates'
import hipersendService from '../../services/hipersend'
import Text from '../Text'
import CircularProgressCustom from '../CircularProgressCustom'
import ConfirmationModal from '../ConfirmationModal'
import { montarFeedbackReenvio } from '../../libs/resendFeedback'
import { buildFailureDigest } from '../../libs/failureDigest'
import LinearProgressCustom from '../LinearProgressCustom'
import TextFieldCustom from '../TextFieldCustom'
import LinkWithQuery from '../LinkWithQuery'
import PeriodSelector from '../shared/report/PeriodSelector'
import ReportChart from '../shared/report/ReportChart'
import Sparkline from '../shared/report/Sparkline'
import { periodToRange, daysBetween } from '../shared/report/periodRange'
import MessageListItem from './MessageListItem'
import FailureDigest from './FailureDigest'
import ImpactoDoReenvio from './ImpactoDoReenvio'
import ConsumoCards from './ConsumoCards'
import { WabaTemplatesContext, HiperPayContext } from './context'
import { GRADE_COLUNAS } from './colunas'
import PainelAoVivo from './aoVivo/PainelAoVivo'
import usePulso from './aoVivo/usePulso'
import AnalisesDoPeriodo from './analises/AnalisesDoPeriodo'
import PainelExportar from './exportar/PainelExportar'
import { corLegivel, textoSobre } from '../../libs/corLegivel'

const OFFSET_PAGINATION = 30

// Preferência de quem abriu o gráfico completo pelo menos uma vez.
const CHART_OPEN_KEY = 'relatorio:grafico-aberto'

// Quem usa o relatório para conferir conteúdo quer a prévia sempre à mão; quem
// usa para acompanhar volume quer mais linhas na tela. A escolha é lembrada,
// como já se faz com o gráfico.
const DENSIDADE_KEY = 'relatorio:densidade'

// Filtra os banners ativos dentro da janela de datas e separa por posição,
// ordenados por `order` — mesma regra que a página de pagamento aplica.
function buildActiveBanners(list) {
  const now = Date.now()
  const active = (Array.isArray(list) ? list : [])
    .filter((b) => b && b.isActive !== false)
    .filter((b) => {
      const startOk = !b.startDate || new Date(b.startDate).getTime() <= now
      const endOk = !b.endDate || new Date(b.endDate).getTime() >= now
      return startOk && endOk
    })
    .sort((a, b) => (a.order || 0) - (b.order || 0))

  return {
    top: active.filter((b) => (b.position || 'top') === 'top'),
    bottom: active.filter((b) => b.position === 'bottom'),
  }
}

export default function Reports() {
  // saldoInfo/usaLinhaDeCredito saíram daqui: o saldo agora vem do endpoint de
  // consumo, junto do gasto do período. refreshSaldo continua, pra atualizar o
  // saldo do contexto (usado no resto do painel) depois de reenviar mensagens.
  const { server, getCredentials, provider, user, refreshSaldo, linkRecarga } = useContext(ApiDataWrapperContext)
  const { api, secret } = getCredentials()
  const theme = useTheme()
  const containerRef = useRef()

  // Período do relatório — afeta cards, gráfico e lista. Presets calculados em BRT.
  const [period, setPeriod] = useState('30d')
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')

  // Range SELECIONADO nos controles (pode estar incompleto no custom).
  const selectedRange = useMemo(() => periodToRange(period, customStart, customEnd), [period, customStart, customEnd])

  // Range APLICADO — o que os dados na tela realmente refletem. Só avança quando
  // o selecionado é válido, então o rótulo nunca descasa dos números exibidos.
  const [applied, setApplied] = useState(() => ({ range: selectedRange, period: '30d' }))
  const rangeRef = useRef(applied.range)
  useEffect(() => { rangeRef.current = applied.range }, [applied])

  const periodLabel = useMemo(() => {
    const p = applied.period
    if (p === 'today') return 'Hoje'
    if (p === '7d') return 'Últimos 7 dias'
    if (p === '30d') return 'Últimos 30 dias'
    if (p === 'custom' && applied.range.startDay && applied.range.endDayInclusive) {
      return `${moment(applied.range.startDay).format('DD/MM/YYYY')} – ${moment(applied.range.endDayInclusive).format('DD/MM/YYYY')}`
    }
    return 'Personalizado'
  }, [applied])

  // Modo ao vivo: ligado por padrão. O topo consulta o pulso a cada 5s e os
  // números do período (contadores, gráfico e a 1ª página da lista) a cada 15s.
  // "Pausar" congela tudo — substitui o antigo switch "Atualizar a cada 10s".
  const [pausado, setPausado] = useState(false)
  const autoTickRef = useRef(false)
  const pageRef = useRef(0)
  const metaOficial = isMetaOfficialProvider(provider)
  const { pulso, atualizadoEm, falhou: pulsoFalhou } = usePulso({ api, secret, ativo: !pausado })

  // Painel "Exportar": aberto a partir do botão da lista ou dos atalhos dos
  // cards de análise (que já abrem na opção certa).
  const [exportar, setExportar] = useState({ aberto: false, ancora: null, tipo: 'erros' })
  const exportarRef = useRef(null)
  const abrirExportar = (tipo = 'erros', ancora = null) =>
    setExportar({ aberto: true, ancora: ancora || exportarRef.current, tipo })

  const [reportData, setReportData] = useState([])
  // Veredito de saúde da conta Meta (vem junto do relatório). Alimenta o banner
  // de alerta de pagamento no topo da lista de mensagens.
  const [accountHealth, setAccountHealth] = useState(null)
  const [messages, setMessages] = useState([])
  const [withinWorkingHours, setWithinWorkingHours] = useState(false)
  const [statistics, setStatistics] = useState({
    success: undefined,
    error: undefined,
    queue: undefined
  })
  const [page, setPage] = useState(0)
  const [phoneLike, setPhoneLike] = useState('')
  const [messageLike, setMessageLike] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  // Motivo de falha selecionado na barra de resumo (null = sem filtro).
  const [motivoAtivo, setMotivoAtivo] = useState(null)
  const [isLoadingMessages, setLoadingMessages] = useState(false)
  const [isReloading, setIsReloading] = useState(false)
  const [isOpenConfirmationModalClearQueue, setIsOpenConfirmationModalClearQueue] = useState(false)
  const [isOpenConfirmationModalClearReport, setIsOpenConfirmationModalClearReport] = useState(false)
  const [isOpenConfirmationModalResendMessagesWithErrors, setIsOpenConfirmationModalResendMessagesWithErrors] = useState(false)
  const [isOpenConfirmationModalDeleteMessagesWithErrors, setIsOpenConfirmationModalDeleteMessagesWithErrors] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null)
  // O gráfico ocupa quase meia tela e empurrava tudo pra baixo. Fica recolhido
  // por padrão, com a taxa de sucesso e a tendência sempre visíveis; a escolha
  // de quem abre é lembrada, pra não ter que abrir de novo a cada visita.
  const [isChartOpen, setIsChartOpen] = useState(() => {
    try {
      return window.localStorage.getItem(CHART_OPEN_KEY) === 'true'
    } catch (e) {
      return false
    }
  })

  const toggleChart = () => {
    setIsChartOpen((aberto) => {
      const proximo = !aberto
      try {
        window.localStorage.setItem(CHART_OPEN_KEY, String(proximo))
      } catch (e) {
        // sem localStorage (aba anônima, storage cheio) a preferência só não persiste
      }
      return proximo
    })
  }
  // Densidade da lista: compacta esconde prévia e atalhos pra caber mais linhas
  // na tela; o motivo do erro permanece nos dois modos. Escolha lembrada, mesmo
  // padrão do gráfico acima.
  const [densidade, setDensidade] = useState(() => {
    try {
      return window.localStorage.getItem(DENSIDADE_KEY) === 'compacto' ? 'compacto' : 'detalhado'
    } catch (e) {
      return 'detalhado'
    }
  })

  const toggleDensidade = () => {
    setDensidade((atual) => {
      const proxima = atual === 'detalhado' ? 'compacto' : 'detalhado'
      try {
        window.localStorage.setItem(DENSIDADE_KEY, proxima)
      } catch (e) {
        // sem localStorage (aba anônima, storage cheio) a preferência só não persiste
      }
      return proxima
    })
  }
  const [isOpenMessagesFilter, setIsOpenMessagesFilter] = useState(false)
  const [perdiodToDelete, setPeriodToDelete] = useState('1d')
  // Janela do reenvio em HORAS. O master aceita de 1 a 120 e assume 24 quando
  // nada e informado — que era o unico valor possivel antes desta escolha.
  const [horasDoReenvio, setHorasDoReenvio] = useState(24)
  // Sem saldo (bloqueio ativo) ou sem nada no período, o PROSSEGUIR fica travado.
  const [reenvioBloqueado, setReenvioBloqueado] = useState(false)
  const [wabaTemplatesByName, setWabaTemplatesByName] = useState({})
  // Consumo do período: gasto, saldo, mix e (quando é dele) o custo na Meta.
  const [consumo, setConsumo] = useState(null)
  const [consumoLoading, setConsumoLoading] = useState(false)
  const consumoReq = useRef(0)
  const [hiperPayProfilesById, setHiperPayProfilesById] = useState({})
  const [sfStats, setSfStats] = useState(null)
  const hasSmartFilter = user?.plan?.functionalities?.includes('SmartFilter')
  const [hiperPayBanners, setHiperPayBanners] = useState({ top: [], bottom: [] })

  // Só vale a pena buscar branding/banners do HiperPay se há mensagem com link
  // de pagamento na página atual.
  const hasHiperPayMessage = messages.some((m) => /%PaymentLink%/.test(m?.message || ''))

  // Falhas da página agrupadas por motivo — é o que a barra acima da lista mostra.
  const gruposDeFalha = useMemo(
    () => buildFailureDigest(messages, { provider }),
    [messages, provider]
  )

  // O motivo escolhido pode desaparecer dos dados sem que a página mude: o
  // auto-refresh traz uma leva nova e aquele problema pode nem existir mais.
  // Em vez de carregar uma seleção que não corresponde a nada, derivamos aqui o
  // motivo que ainda existe — assim a pill, a lista e o contador nunca discordam.
  const motivoEfetivo = useMemo(
    () => (motivoAtivo && gruposDeFalha.some((g) => g.chave === motivoAtivo) ? motivoAtivo : null),
    [motivoAtivo, gruposDeFalha]
  )

  // O filtro é visual, sobre a página já carregada: não refaz busca nem mexe na
  // paginação. Por isso o rótulo da barra diz "nesta página".
  const messagesVisiveis = useMemo(() => {
    if (!motivoEfetivo) return messages
    const grupo = gruposDeFalha.find((g) => g.chave === motivoEfetivo)
    const ids = new Set(grupo.ids)
    return messages.filter((m) => ids.has(m._id))
  }, [messages, gruposDeFalha, motivoEfetivo])

  // O motivo selecionado deixa de existir quando a lista é repaginada — a
  // seleção some junto, senão a tela ficaria filtrada por algo que não está mais lá.
  useEffect(() => { setMotivoAtivo(null) }, [page])

  // Carrega os templates Meta uma única vez (só pra instâncias Meta Oficial),
  // pra preencher o preview da mensagem com o corpo real do template.
  useEffect(() => {
    if (provider !== 'hiperapi-v3-cloud-official' || !api || !secret) return
    let active = true
    ;(async () => {
      try {
        const res = await hipersendService.notificacoesInteligentes.getWabaSnapshot(api, secret, {})
        const templates = res?.data?.templates || []
        if (!active) return
        const byName = {}
        templates.forEach((t) => {
          if (t?.name) byName[String(t.name).toLowerCase()] = t
        })
        setWabaTemplatesByName(byName)
      } catch (error) {
        console.error(error)
      }
    })()
    return () => { active = false }
  }, [api, secret, provider])

  // Consumo do período (só Meta Oficial): quanto custou, o mix por categoria e,
  // pra quem paga a Meta direto, o custo dele lá. Segue o período APLICADO — o
  // mesmo que a lista reflete — pra o número nunca descasar do rótulo na tela.
  // Falha silenciosa: sem isso a faixa some e o relatório continua inteiro.
  useEffect(() => {
    if (provider !== 'hiperapi-v3-cloud-official' || !api || !secret) return
    const range = applied.range
    if (!range || range.incomplete || !range.dateBegin) return

    const reqId = ++consumoReq.current
    setConsumoLoading(true)
    ;(async () => {
      try {
        const res = await hipersendService.notificacoesInteligentes.getConsumo(api, secret, {
          de: range.dateBegin,
          ate: range.dateEnd
        })
        // Trocar de período rápido dispara duas buscas: só a última pinta.
        if (reqId !== consumoReq.current) return
        setConsumo(res || null)
      } catch (error) {
        if (reqId !== consumoReq.current) return
        console.error(error)
        setConsumo(null)
      } finally {
        if (reqId === consumoReq.current) setConsumoLoading(false)
      }
    })()
  }, [api, secret, provider, applied])

  // Carrega branding (profiles) e banners do HiperPay uma vez, só quando a página
  // tem mensagem com link de pagamento. Falha silenciosa: sem isso, o preview cai
  // num fallback sem branding/banners — nunca quebra a lista.
  useEffect(() => {
    if (!hasHiperPayMessage || !api || !secret) return
    let active = true
    ;(async () => {
      try {
        const [profiles, banners] = await Promise.all([
          hipersendService.hiperpay.getProfiles(api, secret),
          hipersendService.hiperpay.getBanners(api, secret),
        ])
        if (!active) return
        const list = Array.isArray(profiles) ? profiles : (profiles?.profiles || [])
        const byId = {}
        list.forEach((p) => { if (p?._id) byId[String(p._id)] = p })
        setHiperPayProfilesById(byId)
        setHiperPayBanners(buildActiveBanners(Array.isArray(banners) ? banners : (banners?.banners || [])))
      } catch (error) {
        console.error(error)
      }
    })()
    return () => { active = false }
  }, [hasHiperPayMessage, api, secret])

  const handleAnimation = () => {
    anime({
      targets: containerRef.current.children,
      translateY: [10, 0],
      opacity: [0, 1],
      delay: anime.stagger(100),
      scale: [0.99, 1]
    })
  }

  // O expediente (banner de horário) segue vindo do informations; o gráfico
  // agora usa a série por período (getChart).
  const getWorkingHours = async () => {
    try {
      if(!api || !secret) return
      const response = await hipersendService.notificacoesInteligentes.getInformations(api, secret)
      setWithinWorkingHours(response?.officeHourService?.withinWorkingHours ?? false)
    } catch (error) {
      console.error(error)
    }
  }

  // Série diária do período -> formato { data: [[dia, erro, sucesso]], dateTimes }
  // que o ReportChart consome. Preenche dias sem envio com zero (eixo contínuo).
  const getChart = async (r) => {
    try {
      if(!api || !secret) return
      const { dateBegin, dateEnd, startDay, endDayInclusive } = r || rangeRef.current
      const { series, accountHealth } = await hipersendService.notificacoesInteligentes.getReport(api, secret, { dateBegin, dateEnd })
      const byDay = {}
      ;(series || []).forEach((row) => { byDay[row.day] = row })
      const dayList = daysBetween(startDay, endDayInclusive)
      const data = dayList.map((day) => {
        const row = byDay[day] || {}
        return [day, row.error || 0, row.success || 0]
      })
      setReportData({ data, dateTimes: dayList })
      // Saúde da conta vem junto do relatório (mesma chamada) — alimenta o
      // banner de alerta de pagamento no topo da lista de mensagens.
      setAccountHealth(accountHealth || null)
    } catch (error) {
      console.error(error)
      setReportData({ data: [], dateTimes: [] })
    }
  }

  const getMessages = async ({ page = 0, phoneLike = undefined, messageLike = undefined, status = undefined, silent = false } = {}) => {
    try {
      if(!api || !secret) return
      const offset = page * OFFSET_PAGINATION
      const { dateBegin, dateEnd } = rangeRef.current

      // No auto-refresh (silent) pulamos o delay e o spinner pra não piscar a lista.
      if (!silent) {
        setLoadingMessages(true)
        await delay(800)
      }
      const response = await hipersendService.notificacoesInteligentes.getMessages(api, secret, { offset, phoneLike, messageLike, status: status || undefined, dateBegin, dateEnd })
      setMessages(response?.messages || [])
      if (!silent) setLoadingMessages(false)
    } catch (error) {
      console.error(error)
      if (!silent) setLoadingMessages(false)
    }
  }

  const getMessagesStatistics = async (r) => {
    try {
      if(!api || !secret) return
      const { dateBegin, dateEnd } = r || rangeRef.current
      const response = await hipersendService.notificacoesInteligentes.getMessagesStatistics(api, secret, { dateBegin, dateEnd })
      setStatistics(response)
    } catch (error) {
      console.error(error)
    }
  }

  const reloadReport = async () => {
    try {
      setIsReloading(true)
      const r = rangeRef.current
      await Promise.all([
        getWorkingHours(),
        getChart(r),
        getMessagesStatistics(r),
        getMessages({ page, phoneLike, messageLike, status: statusFilter }),
      ])
      if (refreshSaldo) await refreshSaldo() // saldo muda com o consumo — refresca junto
      setIsReloading(false)
    } catch (error) {
      setIsReloading(false)
    }
  }

  const handleClearQueue = async () => {
    try {
      if(!api || !secret) return
      await hipersendService.notificacoesInteligentes.clearQueue(api, secret)
      getMessages({ page, status: statusFilter })
      getMessagesStatistics()
    } catch (error) {
      console.error(error)
    }
  }

  const handleClearReport = async () => {
    try {
      if(!api || !secret) return
      await hipersendService.notificacoesInteligentes.clearReport(api, secret)
      getMessages({ page, status: statusFilter })
      getMessagesStatistics()
    } catch (error) {
      console.error(error)
    }
  }

  const handleResendMessagesWithErrors = async () => {
    try {
      if(!api || !secret) return
      const resposta = await hipersendService.notificacoesInteligentes.resendMessagesWithErrors(api, secret, horasDoReenvio)
      // Nem tudo volta pra fila: o que já saiu fica de fora de propósito (reenviar
      // geraria mensagem duplicada pro destinatário). Dizer isso evita o cliente
      // clicar de novo achando que a ação não funcionou.
      setSnackbarMessage(montarFeedbackReenvio(resposta) || 'Mensagens com erro reenviadas.')
      getMessages({ page, status: statusFilter })
      getMessagesStatistics()
    } catch (error) {
      console.error(error)
      // 409: o servidor conferiu de novo e o saldo não cobre — nada voltou pra fila.
      const dados = error?.response?.data
      if (error?.response?.status === 409 && dados?.conferencia) {
        setSnackbarMessage(dados.message || 'O saldo não cobre este reenvio. Recarregue para reenviar.')
        return
      }
      setSnackbarMessage('Falha ao reenviar as mensagens com erro. Tente novamente.')
    }
  }

  const handleDeleteMessagesWithErrors = async () => {
    try {
      if(!api || !secret) return
      await hipersendService.notificacoesInteligentes.deleteMessagesWithErrors(api, secret, perdiodToDelete)
      getMessages({ page, status: statusFilter })
      getMessagesStatistics()
    } catch (error) {
      console.error(error)
    }
  }

  const paginate = page => {
    getMessages({ page, phoneLike, messageLike, status: statusFilter })

    if(page >= 0) {
      setPage(page)
    }
  }

  const nextPage = () => {
    const currentPage = page + 1
    paginate(currentPage)
  }

  const previousPage = () => {
    const currentPage = page - 1
    paginate(currentPage)
  }

  useEffect(() => {
    handleAnimation()
  }, [])

  // Carga inicial + recarga quando muda a instância OU o período aplicado.
  // Custom incompleto (só uma data) não busca — mantém o período anterior na
  // tela até o usuário escolher início e fim.
  useEffect(() => {
    if (!api || !secret) return
    if (selectedRange.incomplete) return

    // Sincroniza o ref ANTES de buscar: getMessages lê rangeRef.current, e o
    // efeito que espelha `applied` só rodaria no próximo render (buscaria o range
    // anterior na troca de período).
    rangeRef.current = selectedRange
    setApplied({ range: selectedRange, period })
    setPage(0)
    // Volta os KPIs ao skeleton pra não exibir os números do período anterior
    // rotulados com o novo enquanto a busca não resolve. O gráfico atualiza no
    // lugar (sem colapsar) — a série nova entra assim que o getChart responde.
    setStatistics({ success: undefined, error: undefined, queue: undefined })
    getWorkingHours()
    getChart(selectedRange)
    getMessagesStatistics(selectedRange)
    getMessages({ page: 0, phoneLike, messageLike, status: statusFilter })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, secret, selectedRange.dateBegin, selectedRange.dateEnd])

  // pageRef sempre com a página atual (o auto-refresh recarrega a mesma página).
  useEffect(() => { pageRef.current = page }, [page])

  // Batida do modo ao vivo a cada 15s: pausa quando a aba está oculta, não
  // sobrepõe chamadas e limpa o intervalo ao pausar/desmontar. A lista só é
  // recarregada na 1ª página — é onde as mensagens novas aparecem; quem foi
  // conferir uma página antiga não tem a tela trocada debaixo do mouse.
  useEffect(() => {
    if (pausado || !api || !secret) return
    const tick = async () => {
      if (typeof document !== 'undefined' && document.hidden) return
      if (autoTickRef.current) return
      autoTickRef.current = true
      try {
        const r = rangeRef.current
        await Promise.all([
          getChart(r),
          getMessagesStatistics(r),
          pageRef.current === 0
            ? getMessages({ page: 0, phoneLike, messageLike, status: statusFilter, silent: true })
            : Promise.resolve(),
          getWorkingHours(),
        ])
      } catch (e) {
        // silencioso — a próxima batida tenta de novo
      } finally {
        autoTickRef.current = false
      }
    }
    const id = setInterval(tick, 15000)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pausado, api, secret, phoneLike, messageLike, statusFilter])

  // Carrega stats do SmartFilter só se o plano inclui a funcionalidade
  useEffect(() => {
    if (!hasSmartFilter || !api || !secret) return
    const days = new Date().getDate()
    hipersendService.smartFilter.getStats(api, secret, { days })
      .then(data => setSfStats(data || null))
      .catch(() => {})
  }, [api, secret, hasSmartFilter])

  return (
    <>
      <MessagesFilterModal
        isOpen={isOpenMessagesFilter}
        onClose={() => setIsOpenMessagesFilter(false)}
        phoneLike={phoneLike}
        messageLike={messageLike}
        statusFilter={statusFilter}
        onFilter={({ phoneLike, messageLike, status }) => {
          setPhoneLike(phoneLike)
          setMessageLike(messageLike)
          setStatusFilter(status)
          setPage(0)
          getMessages({ page: 0, phoneLike, messageLike, status })
        }}
      />
      <ConfirmationModal
        open={isOpenConfirmationModalClearReport}
        title={
          <div className="flex items-center space-x-2">
            <Text component="span">
              <GppMaybeIcon style={{ fontSize: 35, color: theme.palette.primary.main }} />
            </Text>
            <Text component="span" className="font-bold text-xl">
              Limpar mensagens
            </Text>
          </div>
        }
        challengeQuestion="Digite a palavra PROSSEGUIR para prosseguir com a limpeza das mensagens"
        challengeAwnswer="PROSSEGUIR"
        onClose={() => {
          setIsOpenConfirmationModalClearReport(false)
        }}
        onConfirm={handleClearReport}
      >
        <Text className="font-bold">Essa ação é irreversível.</Text>
        <Text>Deseja realmente limpar as mensagens?</Text>
      </ConfirmationModal>
      <ConfirmationModal
        open={isOpenConfirmationModalClearQueue}
        title={
          <div className="flex items-center space-x-2">
            <Text component="span">
              <GppMaybeIcon style={{ fontSize: 35, color: theme.palette.primary.main }} />
            </Text>
            <Text component="span" className="font-bold text-xl">
              Limpar fila
            </Text>
          </div>
        }
        challengeQuestion="Digite a palavra PROSSEGUIR para prosseguir com a limpeza da fila"
        challengeAwnswer="PROSSEGUIR"
        onClose={() => {
          setIsOpenConfirmationModalClearQueue(false)
        }}
        onConfirm={handleClearQueue}
      >
        <Text className="font-bold">Essa ação é irreversível.</Text>
        <Text>Deseja realmente limpar as mensagens que estão na fila?</Text>
      </ConfirmationModal>
      <ConfirmationModal
        open={isOpenConfirmationModalResendMessagesWithErrors}
        title={
          <div className="flex items-center space-x-2">
            <Text component="span">
              <GppMaybeIcon style={{ fontSize: 35, color: theme.palette.primary.main }} />
            </Text>
            <Text component="span" className="font-bold text-xl">
              Reenviar mensagens com erro
            </Text>
          </div>
        }
        challengeQuestion="Digite a palavra PROSSEGUIR para prosseguir com o reenvio das mensagens com erro"
        challengeAwnswer="PROSSEGUIR"
        onClose={() => {
          setIsOpenConfirmationModalResendMessagesWithErrors(false)
        }}
        onConfirm={handleResendMessagesWithErrors}
        bloquearConfirmacao={reenvioBloqueado}
      >
        <Text>Deseja realmente reenviar as mensagens com erro?</Text>
        <Text className="mt-3">Reenviar as falhas de:</Text>
        <Select
          className="mt-2 mb-3 w-1/2"
          size="small"
          value={horasDoReenvio}
          onChange={(event) => setHorasDoReenvio(event.target.value)}
        >
          <MenuItem value={24}>Últimas 24 horas</MenuItem>
          <MenuItem value={48}>Últimos 2 dias</MenuItem>
          <MenuItem value={72}>Últimos 3 dias</MenuItem>
          <MenuItem value={120}>Últimos 5 dias</MenuItem>
        </Select>
        <Text className="opacity-80">
          Mensagens que já chegaram ao destinatário não são reenviadas — isso geraria uma
          segunda mensagem para o mesmo contato. As que a Meta recusou entram no reenvio:
          nesses casos ninguém recebeu nada. O motivo da falha continua no relatório.
        </Text>
        {isOpenConfirmationModalResendMessagesWithErrors && (
          <ImpactoDoReenvio
            api={api}
            secret={secret}
            lastHours={horasDoReenvio}
            linkRecarga={linkRecarga}
            onVeredito={({ bloqueado }) => setReenvioBloqueado(bloqueado)}
          />
        )}
      </ConfirmationModal>
      <PainelExportar
        aberto={exportar.aberto}
        ancora={exportar.ancora}
        tipoInicial={exportar.tipo}
        onFechar={() => setExportar((e) => ({ ...e, aberto: false }))}
        api={api}
        secret={secret}
        provider={provider}
        metaOficial={metaOficial}
        range={applied.range}
        periodLabel={periodLabel}
      />
      <Snackbar
        open={!!snackbarMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={8000}
        onClose={() => setSnackbarMessage('')}
        message={snackbarMessage}
      />
      <ConfirmationModal
        open={isOpenConfirmationModalDeleteMessagesWithErrors}
        title={
          <div className="flex items-center space-x-2">
            <Text component="span">
              <GppMaybeIcon style={{ fontSize: 35, color: theme.palette.primary.main }} />
            </Text>
            <Text component="span" className="font-bold text-xl">
              Deletar mensagens com erro
            </Text>
          </div>
        }
        challengeQuestion="Digite a palavra PROSSEGUIR para prosseguir com a remoção das mensagens com erro"
        challengeAwnswer="PROSSEGUIR"
        onClose={() => {
          setIsOpenConfirmationModalDeleteMessagesWithErrors(false)
        }}
        onConfirm={handleDeleteMessagesWithErrors}
      > 
        <Text>Período de exclusão:</Text>
         <Select
          className="mt-3 mb-3 w-1/3"
          size="small"
          sx={{
            select: {
              '.MuiOutlinedInput-notchedOutline': {
                borderWidth: '10px !important',
              }
            }
          }}
          labelId="hiperapi-version-label"
          id="hiperapi-version"
          value={perdiodToDelete}
          onChange={event => {
            setPeriodToDelete(event.target.value)
          }}
        >
          <MenuItem value="1d">Últimas 24h</MenuItem>
          <MenuItem value="7d">Últimos 7 dias</MenuItem>
          <MenuItem value="all">Desde o Inicío</MenuItem>
        </Select>
        <Text className="font-bold">Essa ação é irreversível.</Text>
        <Text>Deseja realmente remover as mensagens com erro?</Text>
      </ConfirmationModal>
      <div className="relative px-4">
        <div
          className={clsx('absolute top-0 left-0 w-full h-[400px] ', {
            'bg-opacity-[0.02] bg-white': theme.isDark,
            'bg-opacity-[0.05] bg-gray-800': !theme.isDark
          })}
        />
        <div
          className={clsx('absolute top-0 left-0 w-full h-[550px]', {
            'bg-opacity-[0.02] bg-white': theme.isDark,
            'bg-opacity-[0.05] bg-gray-800': !theme.isDark
          })}
        />
        <div ref={containerRef} className="relative max-w-[1100px] w-full mx-auto py-8 md:py-10">
          {/* Topo ao vivo: identidade da instância, farol da conexão, fila em
              andamento, os últimos acontecimentos e os contadores do período.
              O seletor de período mora aqui dentro, no canto do cabeçalho. */}
          <PainelAoVivo
            api={api}
            server={server}
            provider={provider}
            metaOficial={metaOficial}
            pulso={pulso}
            atualizadoEm={atualizadoEm}
            falhou={pulsoFalhou}
            pausado={pausado}
            onAlternarPausa={() => setPausado((p) => !p)}
            statistics={statistics}
            periodLabel={periodLabel}
            seletorDePeriodo={
              <PeriodSelector
                period={period}
                customStart={customStart}
                customEnd={customEnd}
                incomplete={selectedRange.incomplete}
                onChange={({ period: p, customStart: cs, customEnd: ce }) => {
                  setPeriod(p)
                  setCustomStart(cs || '')
                  setCustomEnd(ce || '')
                }}
              />
            }
          />

          {/* Ações da instância. Só a construtiva fica exposta; as três que apagam
              dados vão pro menu — continuam a um clique, com a mesma confirmação
              por digitação, mas não disputam atenção com o resto da tela. */}
          <div className="mt-5 flex items-center gap-2">
            <Button
              onClick={() => {
                // Travado até a conferência do período responder.
                setReenvioBloqueado(true)
                setIsOpenConfirmationModalResendMessagesWithErrors(true)
              }}
              startIcon={<ReplayIcon />}
              variant="outlined"
              size="small"
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                color: corLegivel(theme.palette.primary.main, theme.palette.background.default),
              }}
            >
              Reenviar com erro
            </Button>
            <Button
              onClick={(e) => setActionsMenuAnchor(e.currentTarget)}
              aria-label="Mais ações"
              aria-haspopup="true"
              aria-expanded={Boolean(actionsMenuAnchor)}
              color="inherit"
              size="small"
              sx={{ borderRadius: '10px', minWidth: 40, opacity: 0.7 }}
            >
              <MoreHorizIcon />
            </Button>
            <Menu
              anchorEl={actionsMenuAnchor}
              open={Boolean(actionsMenuAnchor)}
              onClose={() => setActionsMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <MenuItem
                onClick={() => {
                  setActionsMenuAnchor(null)
                  setIsOpenConfirmationModalClearQueue(true)
                }}
              >
                <PlaylistRemoveIcon fontSize="small" className="mr-2 opacity-70" />
                <Text component="span">Limpar fila</Text>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setActionsMenuAnchor(null)
                  setIsOpenConfirmationModalDeleteMessagesWithErrors(true)
                }}
              >
                <DeleteSweepIcon fontSize="small" className="mr-2" style={{ color: '#eb3c3c' }} />
                <Text component="span" style={{ color: '#eb3c3c' }}>Apagar com erro</Text>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setActionsMenuAnchor(null)
                  setIsOpenConfirmationModalClearReport(true)
                }}
              >
                <DeleteIcon fontSize="small" className="mr-2" style={{ color: '#eb3c3c' }} />
                <Text component="span" style={{ color: '#eb3c3c' }}>Limpar mensagens</Text>
              </MenuItem>
            </Menu>
          </div>

          {/* Consumo do período: quanto custou, o mix de modelos e — pra quem
              paga a Meta direto — o custo dele lá. Só Meta Oficial; some
              inteiro quando não há o que mostrar. */}
          {metaOficial && (
            <ConsumoCards dados={consumo} loading={consumoLoading} periodLabel={periodLabel} />
          )}

          {/* Resumo SmartFilter — só aparece pra planos com acesso */}
          {hasSmartFilter && sfStats && sfStats.totals && (
            <div
              className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 px-4 py-2.5 rounded-xl"
              style={{
                background: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`
              }}
            >
              <div className="flex items-center gap-1.5">
                <ShieldIcon style={{ fontSize: 15, color: theme.palette.primary.main, opacity: 0.7 }} />
                <Text className="text-xs font-semibold opacity-60">SmartFilter</Text>
              </div>
              <div className="flex items-center gap-1">
                <Text className="text-xs opacity-45">Filtradas</Text>
                <Text className="text-sm font-bold">{(sfStats.totals.allowed || 0).toLocaleString('pt-BR')}</Text>
              </div>
              <div className="flex items-center gap-1">
                <Text className="text-xs opacity-45">Bloqueadas</Text>
                <Text className="text-sm font-bold" style={{ color: theme.palette.error.main }}>{(sfStats.totals.blocked || 0).toLocaleString('pt-BR')}</Text>
              </div>
              {(sfStats.totals.delayed || 0) > 0 && (
                <div className="flex items-center gap-1">
                  <Text className="text-xs opacity-45">Atrasadas</Text>
                  <Text className="text-sm font-bold" style={{ color: theme.palette.warning.main }}>{sfStats.totals.delayed.toLocaleString('pt-BR')}</Text>
                </div>
              )}
              {/* O saldo saiu daqui: vivia dentro do bloco do SmartFilter, então
                  quem não tinha o recurso nunca via o próprio saldo. Agora ele é
                  um dos cards de consumo acima, com o gasto do período e a
                  projeção ao lado — e aparece para todo mundo. */}
            </div>
          )}

          {/* Desempenho: recolhido, a barra já responde "está indo bem?" com a
              taxa e a tendência. Quem precisa dos números por dia abre — e a
              escolha fica lembrada. */}
          {reportData.data && (() => {
            const rows = reportData.data || []
            const sucessos = rows.map((r) => r[2] || 0)
            const succ = sucessos.reduce((a, n) => a + n, 0)
            const err = rows.reduce((a, r) => a + (r[1] || 0), 0)
            const total = succ + err
            const rate = total > 0 ? ((succ / total) * 100).toFixed(1) : '0'
            const rateOk = parseFloat(rate) >= 90

            return (
              <div className="mt-3">
                <button
                  type="button"
                  onClick={toggleChart}
                  aria-expanded={isChartOpen}
                  className="w-full flex items-center gap-4 px-4 py-2.5 rounded-xl text-left"
                  style={{ border: theme.isDark ? '1px solid #334155' : '1px solid #e2e8f0' }}
                >
                  <span className="shrink-0">
                    <Text component="span" className="text-xs font-semibold uppercase tracking-wider opacity-50 block">
                      Desempenho de envios
                    </Text>
                    <Text
                      component="span"
                      className="text-sm font-bold block"
                      style={{ color: rateOk ? '#059669' : '#eb3c3c' }}
                    >
                      {rate}% de sucesso
                    </Text>
                  </span>
                  {!isChartOpen && (
                    <span className="flex-1 min-w-0 hidden sm:block">
                      <Sparkline data={sucessos} color="#059669" />
                    </span>
                  )}
                  <span className="ml-auto flex items-center gap-1 shrink-0 opacity-60">
                    <Text component="span" className="text-xs font-medium">
                      {isChartOpen ? 'ocultar gráfico' : 'ver gráfico'}
                    </Text>
                    <ExpandMoreIcon
                      style={{
                        fontSize: 18,
                        transform: isChartOpen ? 'rotate(180deg)' : 'none',
                        transition: 'transform .2s',
                      }}
                    />
                  </span>
                </button>

                <Collapse in={isChartOpen} timeout="auto" unmountOnExit>
                  <div className="mt-3">
                    <ReportChart
                      title="Envios por dia"
                      periodLabel={periodLabel}
                      categories={reportData.dateTimes || []}
                      series={[
                        { name: 'Enviados com sucesso', color: '#059669', data: sucessos },
                        { name: 'Erros de envio', color: '#eb3c3c', data: rows.map((r) => r[1]) },
                      ]}
                      headerStat={{ label: 'taxa de sucesso', value: `${rate}%`, good: rateOk }}
                      unitLabel="mensagens"
                      exportFilename="relatorio-envios"
                    />
                  </div>
                </Collapse>
              </div>
            )
          })()}

          {/* Análises do período (só API Oficial): modelos mais disparados e
              números sem WhatsApp, com atalho pro PDF de cada um. */}
          <AnalisesDoPeriodo
            api={api}
            secret={secret}
            range={applied.range}
            metaOficial={metaOficial}
            onExportar={abrirExportar}
          />
          <div className="mt-6">
            {/* Alerta de conta Meta (ex.: método de pagamento pendente). Fica no
                topo da lista porque é aqui que o cliente vê as mensagens falhando
                — em vez de descobrir mensagem por mensagem, ele entende de uma vez
                que o problema é da CONTA e o que fazer. Só para conexões Meta. */}
            {isMetaOfficialProvider(provider) && (
              <MetaAccountHealthBanner accountHealth={accountHealth} className="mb-6" />
            )}
            <div className="md:flex justify-between items-end">
              <div>
                <Text>MENSAGENS</Text>
                <Text className="text-sm">
                  {motivoEfetivo
                    ? `Mostrando ${messagesVisiveis.length} de ${messages.length} mensagens na página ${page + 1}`
                    : `Mostrando ${messages.length} mensagens na página ${page + 1}`}
                </Text>
              </div>
              <div className="flex items-center space-x-4 mt-5 md:mt-0">
                <button
                  className="flex items-center space-x-2"
                  style={{ opacity: isReloading ? 0.3 : 1 }}
                  disabled={isReloading}
                  onClick={reloadReport}
                >
                  {
                    isReloading
                    ? <CircularProgressCustom size={20} />
                    : <Text component="span"><RefreshIcon /></Text>
                  }
                  <Text component="span">Atualizar relatório</Text>
                </button>
                <button
                  className="flex items-center space-x-2"
                  style={{ opacity: isReloading ? 0.3 : 1 }}
                  disabled={isReloading}
                  onClick={() => setIsOpenMessagesFilter(true)}
                >
                  {
                    isReloading
                    ? <CircularProgressCustom size={20} />
                    : <Text component="span"><FilterAltIcon /></Text>
                  }
                  <span className="flex items-center space-x-1">
                    <Text component="span">Filtros</Text>
                    {
                      (phoneLike || messageLike || statusFilter)
                      ? <span
                          className="text-xs font-black text-white px-1.5 py-0.5 rounded-full"
                          style={{ backgroundColor: '#059669' }}
                        >ATIVO</span>
                      : <span
                          className="text-xs font-black px-1"
                          style={{ backgroundColor: theme.palette.primary.main, color: textoSobre(theme.palette.primary.main) }}
                        >NOVO</span>
                    }
                  </span>
                </button>
                <button
                  ref={exportarRef}
                  type="button"
                  onClick={(e) => abrirExportar('erros', e.currentTarget)}
                  className="flex items-center gap-1.5 rounded-[9px] px-3 py-1 text-sm font-bold"
                  style={{
                    color: corLegivel(theme.palette.primary.main, theme.palette.background.default),
                    border: `1px solid ${theme.palette.primary.main}`,
                    background: alpha(theme.palette.primary.main, 0.08),
                  }}
                >
                  <FileDownloadOutlinedIcon style={{ fontSize: 18 }} />
                  Exportar
                </button>
                <button className="flex items-center space-x-2" onClick={toggleDensidade}>
                  <Text component="span">
                    {densidade === 'detalhado' ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
                  </Text>
                  <Text component="span">{densidade === 'detalhado' ? 'Compactar' : 'Detalhar'}</Text>
                </button>
              </div>
            </div>
            {/* Fora do expediente o farol do topo já avisa (e diz quando volta).
                O banner fica só como reserva, pra quando o pulso não chegou. */}
            {
              !withinWorkingHours && !pulso &&
              <div className="my-2">
                <div className={clsx('bg-red-600 bg-opacity-[1] rounded p-2 flex space-x-2')}>
                  <Text component="span">
                    <AccessTimeIcon className="text-white" style={{ fontSize: 30 }} />
                  </Text>
                  <Text className="pt-1 text-white">
                    Essa instância não está no seu expediente e horário de trabalho e não fará envios no momento. <LinkWithQuery to="/messages/scheduler" className="font-bold">Configurar expediente</LinkWithQuery>
                  </Text>
                </div>
              </div>
            }
            <div className="relative mt-2">
              <Collapse in={isReloading || isLoadingMessages} timeout="auto" unmountOnExit>
                <div className="absolute top-0 left-0 w-full">
                  <LinearProgressCustom />
                </div>
              </Collapse>
              <Collapse in={messages.length === 0 && !isLoadingMessages} timeout="auto" unmountOnExit>
                <div className="py-28 flex flex-col items-center justify-center rounded">
                  <img src="/assets/images/cat-sleeping-gray-001.svg" className="opacity-[0.3]" />
                  <Text className="mt-2 opacity-[0.5]">Não há mensagens por aqui</Text>
                  <Link to="/messages/send" className="mt-4 flex items-center space-x-2">
                    <AutoAwesomeIcon style={{ fontSize: 35, color: theme.palette.primary.main }} />
                    <Text component="span" className="font-bold">
                      ENVIAR PRIMEIRA MENSAGEM
                    </Text>
                  </Link>
                </div>
              </Collapse>
              <FailureDigest grupos={gruposDeFalha} ativo={motivoEfetivo} onSelecionar={setMotivoAtivo} />
              <Paper elevation={5}>
                <div>
                  <WabaTemplatesContext.Provider value={wabaTemplatesByName}>
                  <HiperPayContext.Provider value={{ profilesById: hiperPayProfilesById, banners: hiperPayBanners }}>
                  {messagesVisiveis.length > 0 && <ListaCabecalho />}
                  <MessageListContainer>
                    {
                      messagesVisiveis.map((message, index) => (
                        <MessageListItem
                          key={message._id}
                          message={message}
                          densidade={densidade}
                          onResendMessage={() => {
                            getMessages({ page })
                            getMessagesStatistics()
                          }}
                          onDeleteMessage={() => {
                            getMessages({ page })
                            getMessagesStatistics()
                          }}
                        />
                      ))
                    }
                  </MessageListContainer>
                  </HiperPayContext.Provider>
                  </WabaTemplatesContext.Provider>
                </div>
              </Paper>
              <div className="mt-4 flex items-center space-x-4">
                <button
                  className={clsx('p-1 rounded-full bg-opacity-[0.1]', {
                    'bg-white text-white': theme.isDark,
                    'bg-gray-800 text-black': !theme.isDark,
                    'opacity-[0.3]': page === 0
                  })}
                  onClick={previousPage}
                  disabled={page === 0}
                >
                  <ChevronLeftIcon />
                </button>
                <button
                  className={clsx('p-1 rounded-full bg-opacity-[0.1]', {
                    'bg-white text-white': theme.isDark,
                    'bg-gray-800 text-black': !theme.isDark
                  })}
                  onClick={nextPage}
                >
                  <ChevronRightIcon />
                </button>
                <Text className="text-xs font-black">Página {page + 1}</Text>
                {
                  isLoadingMessages &&
                  <CircularProgressCustom size={20} />
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

// Cabeçalho da tabela de mensagens. Os rótulos saíram das 30 linhas e passaram a
// ser escritos uma vez só, aqui em cima — é o que separa uma tabela de uma ficha
// de cadastro repetida 30 vezes. Usa a MESMA grade da linha (colunas.js): é o
// único jeito de o rótulo continuar em cima da coluna que ele nomeia.
//
// Só existe em md+, onde a linha de fato é uma grade; abaixo disso a linha vira
// uma pilha e um cabeçalho de colunas não teria a que se referir.
function ListaCabecalho() {
  const theme = useTheme()

  return (
    <div
      className="hidden lg:grid items-center gap-x-3 px-[14px] py-1.5 text-[9.5px] font-bold uppercase"
      style={{
        gridTemplateColumns: GRADE_COLUNAS,
        letterSpacing: '.1em',
        color: theme.isDark ? '#767c86' : '#85909c',
        backgroundColor: theme.isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        borderBottom: theme.isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
      }}
    >
      <span />
      <span>Status</span>
      <span>Destinatário</span>
      <span>Modelo</span>
      <span>Mensagem</span>
      {/* Ações e envio alinhados à direita porque é assim que o conteúdo delas
          encosta na linha — rótulo à esquerda ficaria apontando pro vazio. */}
      <span className="text-right">Ações</span>
      <span className="text-right">Envio</span>
      <span />
    </div>
  )
}

function MessageListContainer({ children }) {
  return (
    <ol className="divide-y divide-[#8a8a8a37]">
      {children}
    </ol>
  )
}

// Opções de status disponíveis para filtro
const STATUS_OPTIONS = [
  { value: 'success', label: 'Enviado', color: '#059669', icon: <CheckIcon style={{ fontSize: 16 }} /> },
  { value: 'queue', label: 'Em fila', color: '#6b7280', icon: <WatchLaterIcon style={{ fontSize: 16 }} /> },
  { value: 'erro', label: 'Com erro', color: '#eb3c3c', icon: <ReportProblemIcon style={{ fontSize: 16 }} /> },
  { value: 'antiban_delayed', label: 'Anti-ban', color: '#f59e0b', icon: <WatchLaterIcon style={{ fontSize: 16 }} /> },
  { value: 'saldo_insuficiente', label: 'Saldo insuficiente', color: '#f59e0b', icon: <AccountBalanceWalletIcon style={{ fontSize: 16 }} /> },
  { value: 'hiperpay_delayed', label: 'Aguardando link de pagamento', color: '#f59e0b', icon: <WatchLaterIcon style={{ fontSize: 16 }} /> },
  { value: 'suppressed', label: 'Opt-out', color: '#64748b', icon: <GppMaybeIcon style={{ fontSize: 16 }} /> },
  { value: 'smartfilter_delayed', label: 'Adiada (SmartFilter)', color: '#f59e0b', icon: <ShieldIcon style={{ fontSize: 16 }} /> },
  { value: 'smartfilter_pending_approval', label: 'Aguardando conferência', color: '#f59e0b', icon: <ShieldIcon style={{ fontSize: 16 }} /> },
  { value: 'cancelada', label: 'Cancelada', color: '#64748b', icon: <BlockIcon style={{ fontSize: 16 }} /> },
]

function MessagesFilterModal({ isOpen = false, onClose, onFilter, phoneLike = '', messageLike = '', statusFilter = '' }) {
  const theme = useTheme()
  const [phone, setPhone] = useState(phoneLike)
  const [message, setMessage] = useState(messageLike)
  const [selectedStatus, setSelectedStatus] = useState(statusFilter)

  // Sincroniza quando o drawer reabre com valores externos diferentes
  useEffect(() => {
    if (isOpen) {
      setPhone(phoneLike)
      setMessage(messageLike)
      setSelectedStatus(statusFilter)
    }
  }, [isOpen])

  const handleOnFilter = () => {
    const ml = message
    const pl = phone.replace(/\D/g, '')
    setPhone(pl)
    onFilter({
      phoneLike: pl,
      messageLike: ml,
      status: selectedStatus
    })
    onClose()
  }

  const handleClearFilters = () => {
    setPhone('')
    setMessage('')
    setSelectedStatus('')
    onFilter({ phoneLike: '', messageLike: '', status: '' })
    onClose()
  }

  const hasActiveFilters = phone || message || selectedStatus

  return (
    <Drawer
      open={isOpen}
      anchor="right"
      onClose={onClose}
    >
      <div className="w-[100vw] md:w-[750px]">
        <div className="p-4 md:p-12">
          <div className="flex items-center justify-between space-x-4">
            <div className="flex items-center space-x-2">
              <FilterAltIcon style={{ color: theme.palette.primary.main, fontSize: 30 }} />
              <Text className="text-xl font-bold">Filtro de mensagens</Text>
            </div>
            <button onClick={onClose}>
              <CloseIcon />
            </button>
          </div>
          <div className="mt-10">
            <div>
              <div>
                <Text className="text-lg font-medium">Status da mensagem</Text>
                <Text className="text-sm opacity-60 mt-1">Selecione um status para filtrar</Text>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {STATUS_OPTIONS.map(opt => {
                  const isActive = selectedStatus === opt.value
                  return (
                    <Chip
                      key={opt.value}
                      icon={opt.icon}
                      label={opt.label}
                      clickable
                      onClick={() => setSelectedStatus(isActive ? '' : opt.value)}
                      sx={{
                        fontWeight: isActive ? 700 : 500,
                        fontSize: '0.85rem',
                        px: 0.5,
                        border: `1.5px solid ${isActive ? opt.color : 'transparent'}`,
                        backgroundColor: isActive
                          ? `${opt.color}22`
                          : theme.isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                        color: isActive ? opt.color : 'inherit',
                        '& .MuiChip-icon': {
                          color: isActive ? opt.color : 'inherit'
                        },
                        '&:hover': {
                          backgroundColor: `${opt.color}33`,
                        }
                      }}
                    />
                  )
                })}
              </div>
            </div>
            <div className="mt-10">
              <div>
                <Text className="text-lg font-medium">Critérios de busca</Text>
              </div>
              <div className="mt-6">
                <div>
                  <div className="flex items-center space-x-1">
                    <FilterAltIcon style={{ color: theme.palette.primary.main }} />
                    <Text className="text-sm">Número whatsapp</Text>
                  </div>
                </div>
                <div className="mt-3">
                  <TextFieldCustom
                    size="large"
                    placeholder="Ex: 11999888777"
                    helperText="Informe o número whatsapp a ser buscado"
                    label="Número whatsapp"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                  />
                </div>
              </div>
              <div className="mt-8">
                <div>
                  <div className="flex items-center space-x-1">
                    <FilterAltIcon style={{ color: theme.palette.primary.main }} />
                    <Text className="text-sm">Trecho do conteúdo da mensagem</Text>
                  </div>
                </div>
                <div className="mt-3">
                  <TextFieldCustom
                    size="large"
                    placeholder="Ex: nome do contato, código pix, etc"
                    helperText="Nome do contato, código pix, link, etc, qualquer palavra ou código que contenha na mensagem enviada"
                    label="Conteúdo a ser buscado"
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="mt-16 flex items-center space-x-4">
              <Button onClick={handleOnFilter} startIcon={<FilterAltIcon />}>
                APLICAR FILTROS
              </Button>
              {
                hasActiveFilters &&
                <Button
                  onClick={handleClearFilters}
                  startIcon={<CloseIcon />}
                  color="inherit"
                  sx={{ opacity: 0.7 }}
                >
                  LIMPAR FILTROS
                </Button>
              }
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  )
}
